import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useOpportunity, useUpdateOpportunity, useUsers } from '@/hooks/use-api'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePicklistOptions, usePicklistOptionPairs, useStages } from '@/hooks/use-picklists'
import { useToast } from '@/hooks/use-toast'
import { formatDate } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { Save, X } from 'lucide-react'
import type { Opportunity } from '@/types'
import { explainError } from '@/lib/explain-error'

interface OpportunityDetailModalProps {
  opportunityId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const getStageVariant = (
  stage?: string
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' => {
  if (!stage) return 'default'

  switch (stage.toLowerCase()) {
    case 'new':
      return 'default'
    case 'need identified':
      return 'secondary'
    case 'presentation complete':
      return 'secondary'
    case 'initial quote complete':
      return 'warning'
    case 'final quote':
      return 'warning'
    case 'verbal received':
      return 'warning'
    case 'closed won':
      return 'success'
    case 'closed lost':
      return 'destructive'
    case 'service (do not use)':
      return 'destructive'
    default:
      return 'default'
  }
}

export function OpportunityDetailModal({
  opportunityId,
  open,
  onOpenChange,
}: OpportunityDetailModalProps) {
  const { data, isLoading, error } = useOpportunity(opportunityId || '')
  const updateMutation = useUpdateOpportunity()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { toast } = useToast()
  const { stages: stagesData } = useStages()
  const OPPORTUNITY_STAGES = stagesData.map((s) => s.value)
  const OPPORTUNITY_TYPES = usePicklistOptions('opportunity', 'type')
  const LEAD_SOURCES = usePicklistOptions('opportunity', 'lead_source')
  const CONTRACT_TERM_OPTIONS = usePicklistOptionPairs('opportunity', 'contract_term')
  const SALE_TYPES = usePicklistOptions('opportunity', 'sale_type')
  const LOSS_REASONS = usePicklistOptions('opportunity', 'loss_reason')

  const [formData, setFormData] = useState<Partial<Opportunity>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  const opportunity = data
  const users = usersData?.data ?? []

  // Reset form when opportunity data changes or modal opens/closes
  useEffect(() => {
    if (opportunity) {
      setFormData({
        name: opportunity.opportunity_name || opportunity.name,
        stage_name: opportunity.stage_name || opportunity.stage,
        amount: opportunity.amount,
        close_date: opportunity.close_date,
        type: opportunity.type,
        lead_source: opportunity.lead_source,
        owner_id: opportunity.owner_id,
        description: opportunity.description || '',
        // Custom fields from opportunity_extensions (all 27)
        budget_confirmed__c: opportunity.budget_confirmed__c || false,
        discovery_completed__c: opportunity.discovery_completed__c || false,
        roi_analysis_completed__c: opportunity.roi_analysis_completed__c || false,
        sale_type__c: opportunity.sale_type__c || '',
        opportunity_category__c: opportunity.opportunity_category__c || '',
        reason_for_loss__c: opportunity.reason_for_loss__c || '',
        lost_reason_details__c: opportunity.lost_reason_details__c || '',
        competitor__c: opportunity.competitor__c || '',
        competitor_details__c: opportunity.competitor_details__c || '',
        discount__c: opportunity.discount__c,
        contract_term__c: opportunity.contract_term__c,
        contract_start_date__c: opportunity.contract_start_date__c,
        contract_end_date__c: opportunity.contract_end_date__c,
        reseller__c: opportunity.reseller__c || '',
        renewal_opportunity__c: opportunity.renewal_opportunity__c || '',
        billing_contact__c: opportunity.billing_contact__c || '',
        po_number__c: opportunity.po_number__c || '',
        memo__c: opportunity.memo__c || '',
        lead_source_details__c: opportunity.lead_source_details__c || '',
        pending_order_description__c: opportunity.pending_order_description__c || '',
        do_not_run_validation_rule__c: opportunity.do_not_run_validation_rule__c || false,
        of_products__c: opportunity.of_products__c,
        of_renewal_count__c: opportunity.of_renewal_count__c,
        of_renewal_product__c: opportunity.of_renewal_product__c,
        of_files__c: opportunity.of_files__c,
        of_quotes__c: opportunity.of_quotes__c,
      })
    }
    setErrors({})
  }, [opportunity, open])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Opportunity name is required'
    }

    if (!formData.stage_name) {
      newErrors.stage_name = 'Stage is required'
    }

    if (!formData.close_date) {
      newErrors.close_date = 'Close date is required'
    }

    if (formData.amount !== undefined && formData.amount !== null) {
      const amount = parseFloat(String(formData.amount))
      if (isNaN(amount) || amount < 0) {
        newErrors.amount = 'Amount must be a valid positive number'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    if (!opportunityId) return

    try {
      await updateMutation.mutateAsync({
        id: opportunityId,
        data: formData,
      })

      toast({
        title: 'Success',
        description: 'Opportunity updated successfully.',
      })
      onOpenChange(false)
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.error || explainError(error, 'Failed to update opportunity')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    if (opportunity) {
      setFormData({
        name: opportunity.opportunity_name || opportunity.name,
        stage_name: opportunity.stage_name || opportunity.stage,
        amount: opportunity.amount,
        close_date: opportunity.close_date,
        type: opportunity.type,
        lead_source: opportunity.lead_source,
        owner_id: opportunity.owner_id,
        description: opportunity.description || '',
        // Custom fields from opportunity_extensions (all 27)
        budget_confirmed__c: opportunity.budget_confirmed__c || false,
        discovery_completed__c: opportunity.discovery_completed__c || false,
        roi_analysis_completed__c: opportunity.roi_analysis_completed__c || false,
        sale_type__c: opportunity.sale_type__c || '',
        opportunity_category__c: opportunity.opportunity_category__c || '',
        reason_for_loss__c: opportunity.reason_for_loss__c || '',
        lost_reason_details__c: opportunity.lost_reason_details__c || '',
        competitor__c: opportunity.competitor__c || '',
        competitor_details__c: opportunity.competitor_details__c || '',
        discount__c: opportunity.discount__c,
        contract_term__c: opportunity.contract_term__c,
        contract_start_date__c: opportunity.contract_start_date__c,
        contract_end_date__c: opportunity.contract_end_date__c,
        reseller__c: opportunity.reseller__c || '',
        renewal_opportunity__c: opportunity.renewal_opportunity__c || '',
        billing_contact__c: opportunity.billing_contact__c || '',
        po_number__c: opportunity.po_number__c || '',
        memo__c: opportunity.memo__c || '',
        lead_source_details__c: opportunity.lead_source_details__c || '',
        pending_order_description__c: opportunity.pending_order_description__c || '',
        do_not_run_validation_rule__c: opportunity.do_not_run_validation_rule__c || false,
        of_products__c: opportunity.of_products__c,
        of_renewal_count__c: opportunity.of_renewal_count__c,
        of_renewal_product__c: opportunity.of_renewal_product__c,
        of_files__c: opportunity.of_files__c,
        of_quotes__c: opportunity.of_quotes__c,
      })
    }
    setErrors({})
    onOpenChange(false)
  }

  const computeContractEndDate = (startDate: string, termMonths: number): string => {
    const date = new Date(startDate + 'T00:00:00')
    date.setMonth(date.getMonth() + termMonths)
    date.setDate(date.getDate() - 1)
    // Format from LOCAL components — toISOString() would convert to UTC and can shift the day.
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      // Auto-calculate contract end date
      if (field === 'contract_term__c' || field === 'contract_start_date__c') {
        const term = field === 'contract_term__c' ? value : prev.contract_term__c
        const start = field === 'contract_start_date__c' ? value : prev.contract_start_date__c
        if (term && start) {
          const startStr =
            typeof start === 'string' && start.includes('T')
              ? (start.split('T')[0] as string)
              : String(start)
          updated.contract_end_date__c = computeContractEndDate(startStr, term as number)
        }
      }
      return updated
    })
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        aria-describedby="opportunity-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>
              {opportunity?.opportunity_name || opportunity?.name || 'Opportunity Details'}
            </span>
            {opportunity?.stage && (
              <Badge variant={getStageVariant(opportunity.stage)}>{opportunity.stage}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div id="opportunity-description" className="space-y-6">
          {isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              Loading opportunity details...
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-destructive">
              Error loading opportunity: {error.message}
            </div>
          )}

          {opportunity && (
            <>
              {/* Basic Information */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="required">
                      Opportunity Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      aria-required="true"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      className="mt-1"
                    />
                    {errors.name && (
                      <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="account_name">Account Name</Label>
                    <div className="mt-1">
                      {opportunity.account_id && opportunity.account_name ? (
                        <Link
                          to={`/accounts/${opportunity.account_id}`}
                          onClick={() => onOpenChange(false)}
                          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 font-medium"
                          aria-label={`View account: ${opportunity.account_name}`}
                        >
                          {opportunity.account_name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">
                          {opportunity.account_name || '-'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {opportunity.account_id
                        ? 'Click to view account details'
                        : 'Account association cannot be changed'}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={formData.type || ''}
                      onValueChange={(value) => handleChange('type', value)}
                    >
                      <SelectTrigger id="type" className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {OPPORTUNITY_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="lead_source">Lead Source</Label>
                    <Select
                      value={formData.lead_source || ''}
                      onValueChange={(value) => handleChange('lead_source', value)}
                    >
                      <SelectTrigger id="lead_source" className="mt-1">
                        <SelectValue placeholder="Select lead source" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>

              {/* Financial Information */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Financial Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount || ''}
                      onChange={(e) =>
                        handleChange('amount', e.target.value ? parseFloat(e.target.value) : null)
                      }
                      aria-invalid={!!errors.amount}
                      aria-describedby={errors.amount ? 'amount-error' : undefined}
                      className="mt-1"
                    />
                    {errors.amount && (
                      <p id="amount-error" className="text-sm text-destructive mt-1" role="alert">
                        {errors.amount}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="probability">Probability</Label>
                    <Input
                      id="probability"
                      value={opportunity.probability != null ? `${opportunity.probability}%` : '-'}
                      disabled
                      className="mt-1 bg-muted"
                      aria-readonly="true"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-calculated based on stage
                    </p>
                  </div>
                </div>
              </section>

              {/* Sales Process */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Sales Process
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="stage_name" className="required">
                      Stage *
                    </Label>
                    <Select
                      value={formData.stage_name || ''}
                      onValueChange={(value) => handleChange('stage_name', value)}
                    >
                      <SelectTrigger
                        id="stage_name"
                        className="mt-1"
                        aria-required="true"
                        aria-invalid={!!errors.stage_name}
                        aria-describedby={errors.stage_name ? 'stage-error' : undefined}
                      >
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {OPPORTUNITY_STAGES.map((stage) => (
                          <SelectItem key={stage} value={stage}>
                            {stage}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.stage_name && (
                      <p id="stage-error" className="text-sm text-destructive mt-1" role="alert">
                        {errors.stage_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="close_date" className="required">
                      Close Date *
                    </Label>
                    <DatePicker
                      id="close_date"
                      value={formData.close_date}
                      onChange={(v) => handleChange('close_date', v || null)}
                      aria-required="true"
                      aria-invalid={!!errors.close_date}
                      aria-describedby={errors.close_date ? 'close-date-error' : undefined}
                      className="mt-1"
                    />
                    {errors.close_date && (
                      <p
                        id="close-date-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.close_date}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="status">Status</Label>
                    <div className="mt-1">
                      {opportunity.is_closed ? (
                        <Badge
                          variant={
                            opportunity.stage_name === 'Closed Won' ? 'success' : 'destructive'
                          }
                        >
                          {opportunity.stage_name === 'Closed Won' ? 'Won' : 'Lost'}
                        </Badge>
                      ) : (
                        <Badge>Open</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-calculated based on stage
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="owner_id">Opportunity Owner</Label>
                    <Select
                      value={formData.owner_id || ''}
                      onValueChange={(value) => handleChange('owner_id', value)}
                    >
                      <SelectTrigger id="owner_id" className="mt-1">
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...users]
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>

              {/* Description */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Description
                </h3>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                  placeholder="Enter opportunity description..."
                  aria-label="Opportunity description"
                />
              </section>

              {/* Extension Fields - Salesforce Custom Fields */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Custom Fields
                </h3>

                {/* Sales Qualification Checkboxes */}
                <div className="mb-4">
                  <Label className="text-base font-medium mb-2 block">Sales Qualification</Label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="budget_confirmed__c"
                        checked={formData.budget_confirmed__c || false}
                        onCheckedChange={(checked) => handleChange('budget_confirmed__c', checked)}
                        aria-label="Budget confirmed"
                      />
                      <Label htmlFor="budget_confirmed__c" className="font-normal cursor-pointer">
                        Budget Confirmed
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="discovery_completed__c"
                        checked={formData.discovery_completed__c || false}
                        onCheckedChange={(checked) =>
                          handleChange('discovery_completed__c', checked)
                        }
                        aria-label="Discovery completed"
                      />
                      <Label
                        htmlFor="discovery_completed__c"
                        className="font-normal cursor-pointer"
                      >
                        Discovery Completed
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="roi_analysis_completed__c"
                        checked={formData.roi_analysis_completed__c || false}
                        onCheckedChange={(checked) =>
                          handleChange('roi_analysis_completed__c', checked)
                        }
                        aria-label="ROI analysis completed"
                      />
                      <Label
                        htmlFor="roi_analysis_completed__c"
                        className="font-normal cursor-pointer"
                      >
                        ROI Analysis Completed
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Sale Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="sale_type__c">Sale Type</Label>
                    <Select
                      value={formData.sale_type__c || ''}
                      onValueChange={(value) => handleChange('sale_type__c', value)}
                    >
                      <SelectTrigger id="sale_type__c" className="mt-1">
                        <SelectValue placeholder="Select sale type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SALE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="of_products__c">Number of Products</Label>
                    <Input
                      id="of_products__c"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.of_products__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'of_products__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      aria-label="Number of products"
                    />
                  </div>
                </div>

                {/* Loss Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="reason_for_loss__c">Reason for Loss</Label>
                    <Select
                      value={formData.reason_for_loss__c || ''}
                      onValueChange={(value) => handleChange('reason_for_loss__c', value)}
                    >
                      <SelectTrigger id="reason_for_loss__c" className="mt-1">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOSS_REASONS.map((reason) => (
                          <SelectItem key={reason} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mb-4">
                  <Label htmlFor="lost_reason_details__c">Loss Details</Label>
                  <Textarea
                    id="lost_reason_details__c"
                    value={formData.lost_reason_details__c || ''}
                    onChange={(e) => handleChange('lost_reason_details__c', e.target.value)}
                    rows={3}
                    placeholder="Enter details about why the opportunity was lost..."
                    aria-label="Lost reason details"
                    className="mt-1"
                  />
                </div>

                {/* Contract Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="contract_term__c">Contract Term</Label>
                    <select
                      id="contract_term__c"
                      value={formData.contract_term__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'contract_term__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 mt-1"
                      aria-label="Contract term"
                    >
                      <option value="">Select...</option>
                      {CONTRACT_TERM_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="reseller__c">Reseller</Label>
                    <Input
                      id="reseller__c"
                      value={formData.reseller__c || ''}
                      onChange={(e) => handleChange('reseller__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter reseller name"
                      aria-label="Reseller name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contract_start_date__c">Contract Start Date</Label>
                    <DatePicker
                      id="contract_start_date__c"
                      value={formData.contract_start_date__c}
                      onChange={(v) => handleChange('contract_start_date__c', v || null)}
                      className="mt-1"
                      aria-label="Contract start date"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contract_end_date__c">Contract End Date</Label>
                    <DatePicker
                      id="contract_end_date__c"
                      value={formData.contract_end_date__c}
                      onChange={(v) => handleChange('contract_end_date__c', v || null)}
                      className="mt-1"
                      aria-label="Contract end date"
                    />
                  </div>
                </div>

                {/* Additional Custom Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="opportunity_category__c">Opportunity Category</Label>
                    <Input
                      id="opportunity_category__c"
                      value={formData.opportunity_category__c || ''}
                      onChange={(e) => handleChange('opportunity_category__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter category"
                      aria-label="Opportunity category"
                    />
                  </div>

                  <div>
                    <Label htmlFor="renewal_opportunity__c">Renewal Opportunity</Label>
                    <Input
                      id="renewal_opportunity__c"
                      value={formData.renewal_opportunity__c || ''}
                      onChange={(e) => handleChange('renewal_opportunity__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter renewal opportunity ID"
                      aria-label="Renewal opportunity"
                    />
                  </div>

                  {/* Competitor Information */}
                  <div>
                    <Label htmlFor="competitor__c">Competitor</Label>
                    <Input
                      id="competitor__c"
                      value={formData.competitor__c || ''}
                      onChange={(e) => handleChange('competitor__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter competitor name"
                      aria-label="Competitor"
                    />
                  </div>

                  <div>
                    <Label htmlFor="discount__c">Discount (%)</Label>
                    <Input
                      id="discount__c"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.discount__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'discount__c',
                          e.target.value ? parseFloat(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      placeholder="Enter discount percentage"
                      aria-label="Discount percentage"
                    />
                  </div>

                  {/* Additional Contact and Order Information */}
                  <div>
                    <Label htmlFor="billing_contact__c">Billing Contact</Label>
                    <Input
                      id="billing_contact__c"
                      value={formData.billing_contact__c || ''}
                      onChange={(e) => handleChange('billing_contact__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter billing contact"
                      aria-label="Billing contact"
                    />
                  </div>

                  <div>
                    <Label htmlFor="po_number__c">PO Number</Label>
                    <Input
                      id="po_number__c"
                      value={formData.po_number__c || ''}
                      onChange={(e) => handleChange('po_number__c', e.target.value)}
                      className="mt-1"
                      placeholder="Enter purchase order number"
                      aria-label="Purchase order number"
                    />
                  </div>

                  {/* Count Fields */}
                  <div>
                    <Label htmlFor="of_renewal_count__c">Number of Renewals</Label>
                    <Input
                      id="of_renewal_count__c"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.of_renewal_count__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'of_renewal_count__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      placeholder="Enter number of renewals"
                      aria-label="Number of renewals"
                    />
                  </div>

                  <div>
                    <Label htmlFor="of_renewal_product__c">Number of Renewal Products</Label>
                    <Input
                      id="of_renewal_product__c"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.of_renewal_product__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'of_renewal_product__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      placeholder="Enter number of renewal products"
                      aria-label="Number of renewal products"
                    />
                  </div>

                  <div>
                    <Label htmlFor="of_files__c">Number of Files</Label>
                    <Input
                      id="of_files__c"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.of_files__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'of_files__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      placeholder="Enter number of files"
                      aria-label="Number of files"
                    />
                  </div>

                  <div>
                    <Label htmlFor="of_quotes__c">Number of Quotes</Label>
                    <Input
                      id="of_quotes__c"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.of_quotes__c || ''}
                      onChange={(e) =>
                        handleChange(
                          'of_quotes__c',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="mt-1"
                      placeholder="Enter number of quotes"
                      aria-label="Number of quotes"
                    />
                  </div>
                </div>

                {/* Textarea Fields (full width) */}
                <div className="space-y-4 mb-4">
                  <div>
                    <Label htmlFor="competitor_details__c">Competitor Details</Label>
                    <Textarea
                      id="competitor_details__c"
                      value={formData.competitor_details__c || ''}
                      onChange={(e) => handleChange('competitor_details__c', e.target.value)}
                      rows={2}
                      placeholder="Enter details about competitor..."
                      aria-label="Competitor details"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="memo__c">Memo</Label>
                    <Textarea
                      id="memo__c"
                      value={formData.memo__c || ''}
                      onChange={(e) => handleChange('memo__c', e.target.value)}
                      rows={2}
                      placeholder="Enter memo..."
                      aria-label="Memo"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="lead_source_details__c">Lead Source Details</Label>
                    <Textarea
                      id="lead_source_details__c"
                      value={formData.lead_source_details__c || ''}
                      onChange={(e) => handleChange('lead_source_details__c', e.target.value)}
                      rows={2}
                      placeholder="Enter lead source details..."
                      aria-label="Lead source details"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="pending_order_description__c">Pending Order Description</Label>
                    <Textarea
                      id="pending_order_description__c"
                      value={formData.pending_order_description__c || ''}
                      onChange={(e) => handleChange('pending_order_description__c', e.target.value)}
                      rows={2}
                      placeholder="Enter pending order description..."
                      aria-label="Pending order description"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Checkbox for Validation Rule */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="do_not_run_validation_rule__c"
                    checked={formData.do_not_run_validation_rule__c || false}
                    onCheckedChange={(checked) =>
                      handleChange('do_not_run_validation_rule__c', checked)
                    }
                    aria-label="Do not run validation rule"
                  />
                  <Label
                    htmlFor="do_not_run_validation_rule__c"
                    className="font-normal cursor-pointer"
                  >
                    Do Not Run Validation Rule
                  </Label>
                </div>
              </section>

              {/* Additional Information (Read-only) */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Additional Information
                </h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                    <dd className="mt-1">
                      {opportunity.created_date ? formatDate(opportunity.created_date) : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1">
                      {opportunity.last_modified_date
                        ? formatDate(opportunity.last_modified_date)
                        : '-'}
                    </dd>
                  </div>
                </dl>
              </section>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                  aria-label="Cancel editing"
                >
                  <X className="mr-2 h-4 w-4" aria-hidden="true" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  aria-label="Save opportunity changes"
                >
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
